import { Timestamp, addDoc, collection, getDocs, query, where } from "firebase/firestore";

import { db } from "@/apis/config/firebase";
import { http } from "@/apis/config/http";
import type { ManualReport, MedicalReport } from "@/features/imagings/types/reports";

import type { Imaging } from "..";

const medicalReportsCollection = collection(db, "medical_reports");
const shortcutsCollection = collection(db, "shortcuts");

/**
 * Interfaz para crear un shortcut de informe
 */
interface CreateReportShortcutData {
  object: "report";
  url: string;
  expired_at: Timestamp;
  created_at: Timestamp;
  user: string;
  receiver: string;
  active: boolean;
  report_type: "manual" | "ris";
  report_id: string;
}

/**
 * Obtiene informes médicos de RIS basados en accession_number e institution
 * @param accessionNumber - Número de acceso del estudio
 * @param institutionId - ID de la institución
 * @returns Promise<MedicalReport[]> - Lista de informes RIS encontrados
 */
export const getRISMedicalReports = async (
  accessionNumber: string,
  institutionId: string
): Promise<MedicalReport[]> => {
  try {
    const q = query(
      medicalReportsCollection,
      where("accession_number", "==", accessionNumber),
      where("institution", "==", institutionId)
    );

    const querySnapshot = await getDocs(q);
    const reports: MedicalReport[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data() as MedicalReport;
      reports.push({
        id: doc.id,
        accession_number: data.accession_number,
        institution: data.institution,
        patient_name: data.patient_name,
        patient_id: data.patient_id,
        study_date: data.study_date,
        modality: data.modality,
        body_part: data.body_part,
        report_text: data.report_text,
        radiologist: data.radiologist,
        created_at: data.created_at,
        updated_at: data.updated_at,
        status: data.status,
        type: "ris",
        patient_birth: data.patient_birth,
        patient_gender: data.patient_gender,
        patient_email: data.patient_email,
        patient_phone: data.patient_phone,
        patient_identification: data.patient_identification,
        physician_name: data.physician_name,
        physician_specialty: data.physician_specialty,
        referred_institution: data.referred_institution,
        study_name: data.study_name,
        study_type: data.study_type,
        date_report: data.date_report,
        has_report: data.has_report,
      });
    });

    return reports;
  } catch (error) {
    console.error("Error obteniendo informes RIS:", error);
    throw new Error(
      `Error al obtener informes RIS: ${error instanceof Error ? error.message : "Error desconocido"}`
    );
  }
};

/**
 * Obtiene informes manuales desde la colección imaging
 * @param imagingId - ID del estudio de imagen
 * @returns Promise<ManualReport[]> - Lista de informes manuales
 */
export const getManualMedicalReports = async (
  imagingId: string
): Promise<ManualReport[]> => {
  try {
    // Los informes manuales están en el campo medical_report del documento imaging
    // Necesitamos obtener el documento imaging primero
    const imagingDoc = await getDocs(
      query(collection(db, "imagings"), where("uid", "==", imagingId))
    );

    if (imagingDoc.empty) {
      return [];
    }

    const imagingData = imagingDoc.docs[0].data() as Imaging;
    const medicalReports = imagingData.medical_report || [];

    // Solo procesar como informes manuales aquellos que son strings (URLs)
    return medicalReports
      .filter((report: any) => typeof report === "string")
      .map((reportUrl: string, index: number) => ({
        id: `manual_${imagingId}_${index}`,
        filename: reportUrl.split("/").pop() || `report_${index + 1}.pdf`,
        url: reportUrl,
        size: 0,
        created_at: new Date(),
        type: "manual",
        imaging_id: imagingId,
        patient_name: imagingData.patient_name,
        patient_id: imagingData.patient_id,
        modality: imagingData.modality,
        body_part: imagingData.body_part,
      }));
  } catch (error) {
    console.error("Error obteniendo informes manuales:", error);
    throw new Error(
      `Error al obtener informes manuales: ${error instanceof Error ? error.message : "Error desconocido"}`
    );
  }
};

/**
 * Obtiene una URL firmada para un archivo manual
 * @param idFile - ID del archivo (id_file)
 * @param idStudy - ID del estudio (imaging_id)
 * @param hours - Duración de la URL en horas (por defecto 24)
 * @returns Promise<string> - URL firmada para acceder al archivo
 */
export const getSignedUrlForManualReport = async (
  idFile: string,
  idStudy: string,
  hours: number = 24
): Promise<string> => {
  try {
    const response = await http.get<{ data: { url: string } }>(
      `/api/v1/files/${idFile}/${idStudy}`,
      { hours }
    );
    return response.data.url;
  } catch (error) {
    console.error("Error obteniendo URL firmada:", error);
    throw new Error(
      `Error al obtener URL firmada: ${error instanceof Error ? error.message : "Error desconocido"}`
    );
  }
};

/**
 * Obtiene una URL firmada de larga duración para compartir informes manuales
 * @param idFile - ID del archivo (id_file)
 * @param idStudy - ID del estudio (imaging_id)
 * @param hours - Duración de la URL en horas (por defecto 8800)
 * @returns Promise<string> - URL firmada de larga duración
 */
export const getLongTermSignedUrlForManualReport = async (
  idFile: string,
  idStudy: string,
  hours: number = 8800
): Promise<string> => {
  try {
    const response = await http.get<{ data: { url: string } }>(
      `/api/v1/files/${idFile}/${idStudy}`,
      { hours }
    );
    return response.data.url;
  } catch (error) {
    console.error("Error obteniendo URL firmada de larga duración:", error);
    throw new Error(
      `Error al obtener URL firmada de larga duración: ${error instanceof Error ? error.message : "Error desconocido"}`
    );
  }
};

/**
 * Crea un shortcut para un informe médico
 * @param data - Datos del shortcut a crear
 * @returns Promise<string> - ID del shortcut creado
 */
export const createReportShortcut = async (
  data: CreateReportShortcutData
): Promise<string> => {
  try {
    const docRef = await addDoc(shortcutsCollection, data);
    return docRef.id;
  } catch (error) {
    console.error("Error creando shortcut de informe:", error);
    throw new Error(
      `Error al crear shortcut de informe: ${error instanceof Error ? error.message : "Error desconocido"}`
    );
  }
};

/**
 * Registra una referencia de archivo manual en Firestore y sube el archivo a Storage
 * @param data - Datos del archivo incluyendo el archivo binario y metadatos
 * @returns Promise<void>
 */
export const registerFileReference = async (data: {
  file: File;
  id_study: string;
  filename: string;
  responsible: string;
  date_report: string;
  description: string;
}): Promise<void> => {
  try {
    const formData = new FormData();
    formData.append("file", data.file);
    formData.append("id_study", data.id_study);
    formData.append("filename", data.filename);
    formData.append("responsible", data.responsible);
    formData.append("date_report", data.date_report);
    formData.append("description", data.description);

    await http.post("/api/v1/files", formData);
  } catch (error) {
    console.error("Error registrando referencia de archivo:", error);
    throw new Error(
      `Error al registrar archivo: ${error instanceof Error ? error.message : "Error desconocido"}`
    );
  }
};

/**
 * Elimina un archivo manual permanentemente
 * @param idFile - ID del archivo (id_file)
 * @param idStudy - ID del estudio (imaging_id)
 * @returns Promise<void>
 */
export const deleteManualFile = async (
  idFile: string,
  idStudy: string
): Promise<void> => {
  try {
    await http.delete(`/api/v1/files/${idFile}/${idStudy}`);
  } catch (error) {
    console.error("Error eliminando archivo manual:", error);
    throw new Error(
      `Error al eliminar archivo manual: ${error instanceof Error ? error.message : "Error desconocido"}`
    );
  }
};

/**
 * Elimina (limpia) un informe médico de RIS
 * @param reportId - ID del informe
 * @returns Promise<void>
 */
export const deleteRISReport = async (reportId: string): Promise<void> => {
  try {
    await http.delete(`/api/v1/medical/report/${reportId}`);
  } catch (error) {
    console.error("Error eliminando informe RIS:", error);
    throw new Error(
      `Error al eliminar informe RIS: ${error instanceof Error ? error.message : "Error desconocido"}`
    );
  }
};

/**
 * Obtiene todos los informes médicos de un estudio (RIS + Manuales)
 * @param imagingId - ID del estudio de imagen
 * @param accessionNumber - Número de acceso del estudio
 * @param institutionId - ID de la institución
 * @returns Promise<{ ris: MedicalReport[]; manual: ManualReport[] }> - Informes organizados por tipo
 */
export const getAllMedicalReports = async (
  imagingId: string,
  accessionNumber: string,
  institutionId: string
): Promise<{ ris: MedicalReport[]; manual: ManualReport[] }> => {
  try {
    const [risReports, manualReports] = await Promise.all([
      getRISMedicalReports(accessionNumber, institutionId),
      getManualMedicalReports(imagingId),
    ]);

    return {
      ris: risReports,
      manual: manualReports,
    };
  } catch (error) {
    console.error("Error obteniendo todos los informes:", error);
    throw new Error(
      `Error al obtener informes médicos: ${error instanceof Error ? error.message : "Error desconocido"}`
    );
  }
};
